import React from "react";
import { isAllowed } from "helper/acl";
import { compose } from "helper/compose";
import { withTranslation, type WithTranslation } from "react-i18next";
import PageHeader from "../../Common/PageHeader";
import { buildBreadcrumbs } from "helper/breadcrumbs";
import ProfileDelegateForm from "./ProfileDelegateForm";
import type { FlashMessage, UserCustomer, UserData } from "../../../types/state";
import {
  withProfileDelegateData,
  type DelegateUser,
  type PermissionItem,
  type ProfileDelegateActionsAdapter,
  type ProfileDelegateQueryProps,
} from "api/queries/useProfileDelegate";

const ProfileDelegateFormAny = ProfileDelegateForm as unknown as React.ComponentType<
  Record<string, unknown>
>;

interface ProfileDelegatePageOwnProps extends WithTranslation {
  params: { id?: string };
}

interface ProfileDelegatePageStateProps {
  permissions: PermissionItem[];
  permissionsLoaded: boolean;
  delegateUser: DelegateUser | null;
  message?: FlashMessage | Record<string, never>;
  userData?: UserData;
  customerData?: UserCustomer | Record<string, never>;
  adminToken?: string | boolean | null;
}

interface ProfileDelegatePageDispatchProps {
  profileDelegateActions: ProfileDelegateActionsAdapter;
}

type ProfileDelegatePageProps = ProfileDelegatePageOwnProps &
  ProfileDelegatePageStateProps &
  ProfileDelegateQueryProps &
  ProfileDelegatePageDispatchProps;

interface DelegateFormData {
  permissions: string[];
  last_name: string;
  first_name: string;
  email: string;
  [key: string]: unknown;
}

const INITIAL_FORM_DATA: DelegateFormData = {
  permissions: [],
  last_name: "",
  first_name: "",
  email: "",
};

function ProfileDelegatePage({
  t,
  params,
  permissions,
  permissionsLoaded,
  delegateUser,
  message,
  userData,
  customerData,
  adminToken,
  profileDelegateActions,
}: ProfileDelegatePageProps) {
  const [formData, setFormData] = React.useState<DelegateFormData>(INITIAL_FORM_DATA);
  const [formError, setFormError] = React.useState<Record<string, React.ReactNode>>({});

  React.useEffect(() => {
    profileDelegateActions.loadPermissions();
    if (params.id) {
      profileDelegateActions.loadDelegateUser(params.id);
    }
    profileDelegateActions.messageDelegateUser();
    return () => {
      profileDelegateActions.unloadPermissions();
      if (params.id) {
        profileDelegateActions.unloadDelegateUser();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    const msg = message as FlashMessage | undefined;
    const rawErrors =
      msg && msg.type === "error"
        ? typeof msg.content !== "undefined"
          ? msg.content
          : msg.message
        : null;

    if (rawErrors && typeof rawErrors === "object" && !Array.isArray(rawErrors)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFormError(rawErrors as Record<string, React.ReactNode>);
    } else {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setFormError((prev) => (Object.keys(prev).length > 0 ? {} : prev));
    }
  }, [message]);

  React.useEffect(() => {
    if (!params.id || delegateUser === null) {
      return;
    }

    const permissionsNames = Array.isArray(delegateUser.permissions)
      ? delegateUser.permissions
          .map((permission) => permission.permission_name)
          .filter((value): value is string => Boolean(value))
      : [];

    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFormData((prev) => ({
      ...prev,
      last_name: delegateUser.last_name || "",
      first_name: delegateUser.first_name || "",
      email: delegateUser.email || "",
      permissions: Array.from(new Set(permissionsNames)),
    }));
  }, [params.id, delegateUser]);

  const onFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const field = event.target.name;
    const value = event.target.value;

    setFormError((prev) => {
      const next: Record<string, React.ReactNode> = { ...prev };
      if (field === "permissions[]") {
        delete next["permissions[]"];
      } else {
        delete next[field];
      }
      return next;
    });

    setFormData((prev) => {
      if (field === "permissions[]") {
        const newPermissions = prev.permissions.includes(value)
          ? prev.permissions.filter((permission) => permission !== value)
          : prev.permissions.concat(value);
        return { ...prev, permissions: newPermissions };
      }
      return { ...prev, [field]: value };
    });
  };

  const onFormSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError({});

    if (params.id) {
      profileDelegateActions.updateDelegateUser(params.id, formData);
    } else {
      profileDelegateActions.createDelegateUser(formData);
    }
  };

  const breadcrumbs = buildBreadcrumbs(
    t,
    params.id ? "profileDelegateUpdate" : "profileDelegateAdd"
  );

  if (!isAllowed("webservice.delegate")) {
    return (
      <div className="Page AccountPage">
        <div className="Page-content AccountPage-content">
          <PageHeader
            breadcrumbs={breadcrumbs}
            title={t("common.forbiden")}
            className="PageHero--forbidden"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="Page AccountPage DelegatePageShell">
      <div className="Page-content AccountPage-content">
        <PageHeader
          breadcrumbs={breadcrumbs}
          title={t("profile.delegate.title")}
          bannerClassName="AccountBanner"
          flash={message}
        />

        <ProfileDelegateFormAny
          onChange={onFormChange}
          onSubmit={onFormSubmit}
          formData={formData}
          formError={formError}
          userData={userData}
          customerData={customerData}
          message={message}
          readonly={!!adminToken}
          permissions={permissions}
          permissionsLoaded={permissionsLoaded}
          delegateUser={delegateUser}
        />
      </div>
    </div>
  );
}

export default compose(
  withTranslation(),
  (Component: React.ComponentType<ProfileDelegatePageProps>) =>
    withProfileDelegateData(Component, { delegateUsers: false })
)(ProfileDelegatePage);
